import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { NonPersonnel, NonPersonnelEditResource, NonPersonnelViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { NonPersonnelPageBody } from "./NonPersonnelPageBody/NonPersonnelPageBody";
import { NonPersonnelPageHeader } from "./NonPersonnelPageHeader";

const routeName = "nonPersonnel";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const nonPersonnelPageRoute: PageRoute<Params> = {
  handle: "Non-Personnel",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const NonPersonnelPage = () => {
  const ROOT_PERMISSION: Permission = Permission.NonPersonnel;
  const { params } = useParams(routeName, Params);
  const navigate = useNavigate();
  const [nonPersonnel, setNonPersonnel] = useState<NonPersonnel>({} as NonPersonnel);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [nonPersonnel] });
  const [viewResource, setViewResource] = useState<NonPersonnelViewResource>({});
  const [editResource, setEditResource] = useState<NonPersonnelEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.nonPersonnels.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit")
      ApiService.nonPersonnels.getEditResource().then((response) => {
        setEditResource(response);
      });
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${nonPersonnelPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.nonPersonnels.get(id).then((response) => {
      setNonPersonnel(response);
      resetUnsavedChanges([response]);
    });
  };

  return (
    <Page
      isForm
      header={
        <NonPersonnelPageHeader
          rootPermission={ROOT_PERMISSION}
          handle={nonPersonnelPageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          nonPersonnel={nonPersonnel}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
        />
      }
    >
      <NonPersonnelPageBody
        rootPermission={ROOT_PERMISSION}
        nonPersonnel={nonPersonnel}
        setNonPersonnel={setNonPersonnel}
        crudMode={params.crudMode}
        viewResource={viewResource}
        editResource={editResource}
        validationErrors={validationErrors}
        onEdit={() => reroute(params.id, "edit")}
      />

      <Outlet />
    </Page>
  );
};
